/**
 * @module @bq-core/lib
 * @version 1.0.7
*/
import { buildPoseidon as buildPoseidon$1 } from 'circomlibjs';
import { Group } from '@semaphore-protocol/group';
import { groth16 } from 'snarkjs';
import { formatBytes32String, toUtf8Bytes } from '@ethersproject/strings';
import { generateProof, verifyProof } from '@semaphore-protocol/proof';
import { AbiCoder, keccak256, getAddress } from 'ethers';
import { BigNumber } from '@ethersproject/bignumber';
import { zeroPad } from '@ethersproject/bytes';
import { keccak256 as keccak256$1 } from '@ethersproject/keccak256';
import axios from 'axios';
import { Contract } from '@ethersproject/contracts';
import { JsonRpcProvider, AnkrProvider, PocketProvider, EtherscanProvider, CloudflareProvider, AlchemyProvider, InfuraProvider } from '@ethersproject/providers';
import { Identity } from '@semaphore-protocol/identity';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

let poseidon;
function buildPoseidon() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!poseidon)
            poseidon = yield buildPoseidon$1();
        return (inputs) => {
            const hash = poseidon(inputs.map((x) => BigInt(x)));
            const hashStr = poseidon.F.toString(hash);
            return BigInt(hashStr);
        };
    });
}

/**
 * Generates a proof of knowledge of a solution to a Block Qualified test
 * @param identity The Semaphore identity that will be associated with the solution.
 * @param testAnswers: The answers that the user provided for this test, comprised of the multiple choice and open answers.
 * @param testVariables: The variables that define the test and its grading.
 * @param identityGroup The Semaphore group or its Merkle proof for the corresponding identity group. When providing a passing solution, this group is the credentials group. Otherwise, it is the no credentials group.
 * @param gradeGroup The Semaphore group or its Merkle proof for the grade group.
 * @param testId The ID of the test being solved, used to compute the zero leaf of the Merkle trees.
 * @param snarkArtifacts The SNARK artifacts.
 * @returns The test solution proof ready to be verified.
 */
function generateTestProof({ trapdoor, nullifier }, { multipleChoiceAnswers, openAnswers }, { minimumGrade, multipleChoiceWeight, nQuestions, multipleChoiceRoot, openAnswersHashesRoot, openAnswersHashes }, identityGroup, gradeGroup, testPassed, snarkArtifacts, testId) {
    return __awaiter(this, void 0, void 0, function* () {
        let identityMerkleProof;
        let gradeMerkleProof;
        let emptyLeaf;
        if ("depth" in identityGroup) {
            emptyLeaf = identityGroup.zeroValue;
            identityGroup.addMember(emptyLeaf);
            identityMerkleProof = identityGroup.generateMerkleProof(identityGroup.members.length - 1);
        }
        else {
            if (testId === undefined) {
                throw new Error("The test ID was not provided");
            }
            emptyLeaf = (new Group(testId, MAX_TREE_DEPTH)).root;
            identityMerkleProof = identityGroup;
        }
        if ("depth" in gradeGroup) {
            emptyLeaf = gradeGroup.zeroValue;
            gradeGroup.addMember(gradeGroup.zeroValue);
            gradeMerkleProof = gradeGroup.generateMerkleProof(gradeGroup.members.length - 1);
        }
        else {
            if (testId === undefined) {
                throw new Error("The test ID was not provided");
            }
            emptyLeaf = (new Group(testId, MAX_TREE_DEPTH)).root;
            gradeMerkleProof = gradeGroup;
        }
        const testHeight = Math.sqrt(multipleChoiceAnswers.length || openAnswers.length);
        if (!snarkArtifacts) {
            snarkArtifacts = {
                wasmFilePath: `https://blockqualified.s3.us-east-2.amazonaws.com/test${testHeight}.wasm`,
                zkeyFilePath: `https://blockqualified.s3.us-east-2.amazonaws.com/test${testHeight}.zkey`
            };
        }
        const { proof, publicSignals } = yield groth16.fullProve({
            minimumGrade,
            multipleChoiceWeight,
            nQuestions,
            multipleChoiceAnswers,
            multipleChoiceRoot,
            openAnswers,
            openAnswersHashes,
            openAnswersHashesRoot,
            identityNullifier: nullifier,
            identityTrapdoor: trapdoor,
            identityTreeEmptyLeaf: emptyLeaf,
            identityTreePathIndices: identityMerkleProof.pathIndices,
            identityTreeSiblings: identityMerkleProof.siblings,
            gradeTreeEmptyLeaf: emptyLeaf,
            gradeTreePathIndices: gradeMerkleProof.pathIndices,
            gradeTreeSiblings: gradeMerkleProof.siblings
        }, snarkArtifacts.wasmFilePath, snarkArtifacts.zkeyFilePath);
        return {
            identityCommitment: publicSignals[1].toString(),
            newIdentityTreeRoot: publicSignals[3].toString(),
            gradeCommitment: publicSignals[5].toString(),
            newGradeTreeRoot: publicSignals[7].toString(),
            testPassed,
            publicSignals,
            proof: packProof(proof)
        };
    });
}

/**
 * Generates a proof of knowledge of a solution to a Block Qualified test, while also proving ownership of a different credential.
 * @param identity The Semaphore identity that will be associated with the solution.
 * @param testAnswers: The answers that the user provided for this test, comprised of the multiple choice and open answers.
 * @param testVariables: The variables that define the test and its grading.
 * @param requiredCredentialsGroup The Semaphore credentials group or its Merkle proof of the credential the user already owns.
 * @param testIdentityGroup The Semaphore group or its Merkle proof for the corresponding identity group. When providing a passing solution, this group is the credentials group. Otherwise, it is the no credentials group.
 * @param gradeGroup The Semaphore group or its Merkle proof for the grade group.
 * @param testSnarkArtifacts The SNARK artifacts for the Test proof.
 * @param semaphoreSnarkArtifacts The SNARK artifacts for the Semaphore proof.
 * @param testId The ID of the test being solved, used to compute the zero leaf of the Merkle trees.
 * @returns The test solution proof ready to be verified.
 */
function generateCredentialRestrictedTestProof(identity, testAnswers, testVariables, testIdentityGroup, gradeGroup, requiredCredentialsGroup, testPassed, testSnarkArtifacts, semaphoreSnarkArtifacts, testId) {
    return __awaiter(this, void 0, void 0, function* () {
        const testFullProof = yield generateTestProof(identity, testAnswers, testVariables, testIdentityGroup, gradeGroup, testPassed, testSnarkArtifacts, testId);
        const externalNullifier = formatBytes32String("bq-credential-restricted-test"); // 0x62712d63726564656e7469616c2d726573747269637465642d74657374000000
        const abi = new AbiCoder();
        const encodedSignalPreimage = abi.encode(["uint", "uint", "uint", "uint"], [
            testFullProof.identityCommitment,
            testFullProof.newIdentityTreeRoot,
            testFullProof.gradeCommitment,
            testFullProof.newGradeTreeRoot
        ]);
        const signal = BigInt(keccak256(encodedSignalPreimage));
        const semaphoreFullProof = yield generateProof(identity, requiredCredentialsGroup, externalNullifier, signal, semaphoreSnarkArtifacts);
        return {
            testFullProof,
            semaphoreFullProof
        };
    });
}

/**
 * Generates a proof of knowledge of a solution to a Block Qualified test, while also proving a grade over a certain threshold for a given credential
 * @param identity The Semaphore identity that will be associated with the solution.
 * @param testAnswers: The answers that the user provided for this test, comprised of the multiple choice and open answers
 * @param testIdentityGroup The Semaphore group or its Merkle proof for the corresponding identity group. When providing a passing solution, this group is the credentials group. Otherwise, it is the no credentials group.
 * @param testGradeGroup The Semaphore group or its Merkle proof for the grade group.
 * @param gradeClaimGroupOrMerkleProof The Semaphore group or its Merkle proof for the grade commitment of the user within the grade group for the credential they already own.
 * @param gradeClaimThreshold The grade the user claims to have obtained or surpassed for the credential they already own.
 * @param gradeClaimCommitmentOrTestGradingVariables The user's grade commitment within the tree or the test variables defining the test for the credential they already own.
 * @param testSnarkArtifacts The SNARK artifacts for the Test proof.
 * @param gradeClaimSnarkArtifacts The SNARK artifacts for the grade claim proof.
 * @param testId The ID of the test being solved, used to compute the zero leaf of the Merkle trees
 * @returns The test solution proof ready to be verified.
 */
function generateGradeRestrictedTestProof(identity, testAnswers, testVariables, testIdentityGroup, testGradeGroup, gradeClaimGroupOrMerkleProof, gradeClaimThreshold, gradeClaimCommitmentOrTestGradingVariables, testPassed, testSnarkArtifacts, gradeClaimSnarkArtifacts, testId) {
    return __awaiter(this, void 0, void 0, function* () {
        const testFullProof = yield generateTestProof(identity, testAnswers, testVariables, testIdentityGroup, testGradeGroup, testPassed, testSnarkArtifacts, testId);
        const externalNullifier = formatBytes32String("bq-grade-restricted-test"); // 0x62712d67726164652d726573747269637465642d746573740000000000000000
        const abi = new AbiCoder();
        const encodedSignalPreimage = abi.encode(["uint", "uint", "uint", "uint"], [
            testFullProof.identityCommitment,
            testFullProof.newIdentityTreeRoot,
            testFullProof.gradeCommitment,
            testFullProof.newGradeTreeRoot
        ]);
        const signal = BigInt(keccak256(encodedSignalPreimage));
        const gradeClaimFullProof = yield generateGradeClaimProof(identity, gradeClaimGroupOrMerkleProof, gradeClaimThreshold, externalNullifier, signal, gradeClaimCommitmentOrTestGradingVariables, gradeClaimSnarkArtifacts);
        return {
            testFullProof,
            gradeClaimFullProof
        };
    });
}

/**
 * Generates a Semaphore proof of credential ownership.
 * @param identity The Semaphore identity.
 * @param groupOrMerkleProof The Semaphore group or its Merkle proof.
 * @param externalNullifier The external nullifier.
 * @param signal The Semaphore signal.
 * @param snarkArtifacts The SNARK artifacts.
 * @returns The Semaphore proof ready to be verified.
 */
function generateCredentialOwnershipProof(identity, groupOrMerkleProof, externalNullifier, signal, snarkArtifacts) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!snarkArtifacts) {
            snarkArtifacts = {
                wasmFilePath: `https://www.trusted-setup-pse.org/semaphore/${MAX_TREE_DEPTH}/semaphore.wasm`,
                zkeyFilePath: `https://www.trusted-setup-pse.org/semaphore/${MAX_TREE_DEPTH}/semaphore.zkey`
            };
        }
        return generateProof(identity, groupOrMerkleProof, externalNullifier, signal, snarkArtifacts);
    });
}

const abi$1 = new AbiCoder();
function decodeTestCredentialData(data) {
    const decodedData = abi$1.decode([
        "uint8",
        "uint8",
        "uint8",
        "uint8",
        "uint32",
        "address",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256"
    ], data);
    return {
        testHeight: parseFloat(decodedData[0].toString()),
        minimumGrade: parseFloat(decodedData[1].toString()),
        multipleChoiceWeight: parseFloat(decodedData[2].toString()),
        nQuestions: parseFloat(decodedData[3].toString()),
        timeLimit: parseFloat(decodedData[4].toString()),
        admin: decodedData[5].toString(),
        requiredCredential: parseFloat(decodedData[6].toString()),
        requiredCredentialGradeThreshold: parseFloat(decodedData[7].toString()),
        multipleChoiceRoot: decodedData[8].toString(),
        openAnswersHashesRoot: decodedData[9].toString(),
        testRoot: decodedData[10].toString(),
        testParameters: decodedData[11].toString(),
        nonPassingTestParameters: decodedData[12].toString()
    };
}
function decodeLegacyCredentialData(data) {
    const decodedData = abi$1.decode([
        "uint80",
        "uint80",
        "uint80",
        "uint256",
        "uint256",
        "uint256",
        "uint256"
    ], data);
    return {
        credentialState: {
            gradeTreeIndex: decodedData[0].toNumber(),
            credentialsTreeIndex: decodedData[1].toNumber(),
            noCredentialsTreeIndex: decodedData[2].toNumber(),
            gradeTreeRoot: decodedData[3].toString(),
            credentialsTreeRoot: decodedData[4].toString(),
            noCredentialsTreeRoot: decodedData[5].toString(),
        },
        minimumGrade: decodedData[6].toNumber()
    };
}

const abi = new AbiCoder();
function encodeTestCredential(testHeight, minimumGrade, multipleChoiceWeight, nQuestions, timeLimit, admin, requiredCredential, requiredCredentialGradeThreshold, multipleChoiceRoot, openAnswersHashesRoot) {
    // Ensures it was given a valid address
    admin = getAddress(admin);
    return abi.encode([
        "uint8",
        "uint8",
        "uint8",
        "uint8",
        "uint32",
        "address",
        "uint256",
        "uint256",
        "uint256",
        "uint256"
    ], [
        testHeight,
        minimumGrade,
        multipleChoiceWeight,
        nQuestions,
        timeLimit,
        admin,
        requiredCredential,
        requiredCredentialGradeThreshold,
        multipleChoiceRoot,
        openAnswersHashesRoot
    ]);
}
function encodeLegacyCredential(gradeTreeIndex, credentialsTreeIndex, noCredentialsTreeIndex, gradeTreeRoot, credentialsTreeRoot, noCredentialsTreeRoot, minimumGrade) {
    return minimumGrade ?
        abi.encode([
            "uint80",
            "uint80",
            "uint80",
            "uint256",
            "uint256",
            "uint256",
            "uint256"
        ], [
            gradeTreeIndex,
            credentialsTreeIndex,
            noCredentialsTreeIndex,
            gradeTreeRoot,
            credentialsTreeRoot,
            noCredentialsTreeRoot,
            minimumGrade
        ])
        :
            abi.encode([
                "uint80",
                "uint80",
                "uint80",
                "uint256",
                "uint256",
                "uint256",
            ], [
                gradeTreeIndex,
                credentialsTreeIndex,
                noCredentialsTreeIndex,
                gradeTreeRoot,
                credentialsTreeRoot,
                noCredentialsTreeRoot
            ]);
}
function encodeTestFullProof(testFullProof) {
    return abi.encode([
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256[8]",
        "bool"
    ], [
        testFullProof.identityCommitment,
        testFullProof.newIdentityTreeRoot,
        testFullProof.gradeCommitment,
        testFullProof.newGradeTreeRoot,
        testFullProof.proof,
        testFullProof.testPassed
    ]);
}
function encodeCredentialRestrictedTestFullProof({ testFullProof, semaphoreFullProof }) {
    return abi.encode([
        "uint256",
        "uint256",
        "uint256[8]",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256[8]",
        "bool",
    ], [
        semaphoreFullProof.merkleTreeRoot,
        semaphoreFullProof.nullifierHash,
        semaphoreFullProof.proof,
        testFullProof.identityCommitment,
        testFullProof.newIdentityTreeRoot,
        testFullProof.gradeCommitment,
        testFullProof.newGradeTreeRoot,
        testFullProof.proof,
        testFullProof.testPassed
    ]);
}
function encodeGradeRestrictedTestFullProof({ testFullProof, gradeClaimFullProof }) {
    return abi.encode([
        "uint256",
        "uint256",
        "uint256[8]",
        "uint256",
        "uint256",
        "uint256",
        "uint256",
        "uint256[8]",
        "bool",
    ], [
        gradeClaimFullProof.gradeTreeRoot,
        gradeClaimFullProof.nullifierHash,
        gradeClaimFullProof.proof,
        testFullProof.identityCommitment,
        testFullProof.newIdentityTreeRoot,
        testFullProof.gradeCommitment,
        testFullProof.newGradeTreeRoot,
        testFullProof.proof,
        testFullProof.testPassed
    ]);
}

function generateMultipleChoiceAnswers(multipleChoiceAnswers, testHeight) {
    if (multipleChoiceAnswers.length > Math.pow(2, testHeight)) {
        throw new Error("More answers were given than supported");
    }
    const answersArray = new Array(Math.pow(2, testHeight)).fill('0');
    answersArray.forEach((_, i) => {
        if (i < multipleChoiceAnswers.length) {
            if (Array.isArray(multipleChoiceAnswers[i])) {
                answersArray[i] = multipleChoiceAnswers[i].sort().join('');
            }
            else {
                answersArray[i] = multipleChoiceAnswers[i].toString();
            }
        }
    });
    return answersArray;
}
function generateOpenAnswers(openAnswers, testHeight) {
    if (openAnswers.length > Math.pow(2, testHeight)) {
        throw new Error("More answers were given than supported");
    }
    const resultsArray = new Array(Math.pow(2, testHeight)).fill(hash(""));
    resultsArray.forEach((_, i) => {
        if (i < openAnswers.length) {
            resultsArray[i] = hash(openAnswers[i]);
        }
    });
    return resultsArray;
}

/**
 * Finds the grade a user obtained from their identity and grade group by doing a brute force search through all the possible grades they could obtain.
 * @param gradeGroup The Semaphore group for the grade group.
 * @param identity The Semaphore identity that will be associated with the solution.
 * @param multipleChoiceWeight: Contribution of the multiple choice component towards the final grade.
 * @param nQuestions: Number of questions that make up the open answer component.
 * @returns The grade the user obtained.
 */
function getGradeCommitment({ trapdoor, nullifier }, gradeGroup, multipleChoiceWeight, nQuestions) {
    return __awaiter(this, void 0, void 0, function* () {
        let poseidon = yield buildPoseidon();
        let identitySecret = poseidon([nullifier, trapdoor]);
        let gradeCommitmentValue;
        let gradeCommitmentIndex;
        let grade;
        var i = 0;
        while (i <= nQuestions) {
            grade = Math.floor((100 - multipleChoiceWeight) * i / nQuestions);
            gradeCommitmentValue = poseidon([identitySecret, grade]);
            gradeCommitmentIndex =
                gradeGroup.indexOf(gradeCommitmentValue) === -1 ?
                    gradeGroup.indexOf(gradeCommitmentValue.toString()) :
                    gradeGroup.indexOf(gradeCommitmentValue);
            if (gradeCommitmentIndex !== -1) {
                return {
                    gradeCommitmentValue,
                    gradeCommitmentIndex,
                    grade: grade * nQuestions
                };
            }
            grade += multipleChoiceWeight;
            gradeCommitmentValue = poseidon([identitySecret, grade]);
            gradeCommitmentIndex =
                gradeGroup.indexOf(gradeCommitmentValue) === -1 ?
                    gradeGroup.indexOf(gradeCommitmentValue.toString()) :
                    gradeGroup.indexOf(gradeCommitmentValue);
            if (gradeCommitmentIndex !== -1) {
                return {
                    gradeCommitmentValue,
                    gradeCommitmentIndex,
                    grade
                };
            }
            i++;
        }
        throw new Error("The user did not obtain a grade for this test");
    });
}

function hash(message) {
    if (message === "") {
        return (BigInt(keccak256(formatBytes32String(message))) >> BigInt(8)).toString();
    }
    if (typeof message === 'string' && isNaN(message)) {
        if (toUtf8Bytes(message).length > 31) {
            return (BigInt(keccak256$1(toUtf8Bytes(message))) >> BigInt(8)).toString();
        }
        message = formatBytes32String(message);
    }
    message = BigNumber.from(message).toTwos(256).toHexString();
    message = zeroPad(message, 32);
    return (BigInt(keccak256$1(message)) >> BigInt(8)).toString();
}

/**
 * Packs a proof into a format compatible with Block Qualified.
 * @param originalProof The proof generated with SnarkJS.
 * @returns The proof compatible with Block Qualified and Semaphore.
 */
function packProof(originalProof) {
    return [
        originalProof.pi_a[0],
        originalProof.pi_a[1],
        originalProof.pi_b[0][1],
        originalProof.pi_b[0][0],
        originalProof.pi_b[1][1],
        originalProof.pi_b[1][0],
        originalProof.pi_c[0],
        originalProof.pi_c[1]
    ];
}

function pairwiseHash(hash, array) {
    const arrayHash = [];
    for (let i = 0; i < array.length; i = i + 2) {
        arrayHash.push(hash([array[i], array[i + 1]]));
    }
    return arrayHash;
}
function rootFromLeafArray(hash, leafArray) {
    const depth = Math.log(leafArray.length) / Math.log(2);
    const tree = Array(depth);
    tree[depth - 1] = pairwiseHash(hash, leafArray);
    for (let j = depth - 2; j >= 0; j--) {
        tree[j] = pairwiseHash(hash, tree[j + 1]);
    }
    return tree[0][0];
}

/**
 * Unpacks a proof into its original form.
 * @param proof The proof compatible with Block Qualified and Semaphore.
 * @returns The proof compatible with SnarkJS.
 */
function unpackProof(proof) {
    return {
        pi_a: [proof[0], proof[1]],
        pi_b: [
            [proof[3], proof[2]],
            [proof[5], proof[4]]
        ],
        pi_c: [proof[6], proof[7]],
        protocol: "groth16",
        curve: "bn128"
    };
}

/**
 * Generates a proof claiming to have obtained a grade above a certain threshold.
 * @param identity The Semaphore identity that is associated with this grade obtained.
 * @param gradeGroupOrMerkleProof The Semaphore group or its Merkle proof for the grade commitment of the user within the grade group.
 * @param gradeThreshold The grade the user claims to have obtained or surpassed.
 * @param signal The Semaphore signal.
 * @param externalNullifier The external nullifier.
 * @param gradeCommitmentOrTestGradingVariables The user's grade commitment within the tree or the test variables defining the test.
 * @param snarkArtifacts The SNARK artifacts.
 * @returns The grade claim proof ready to be verified.
 */
function generateGradeClaimProof(identity, gradeGroupOrMerkleProof, gradeThreshold, externalNullifier, signal, gradeCommitmentOrTestGradingVariables, snarkArtifacts) {
    return __awaiter(this, void 0, void 0, function* () {
        let gradeMerkleProof;
        let gradeCommitment;
        if (!snarkArtifacts) {
            snarkArtifacts = {
                wasmFilePath: `https://blockqualified.s3.us-east-2.amazonaws.com/gradeClaim.wasm`,
                zkeyFilePath: `https://blockqualified.s3.us-east-2.amazonaws.com/gradeClaim.zkey`
            };
        }
        if ("depth" in gradeGroupOrMerkleProof) {
            if ("nQuestions" in gradeCommitmentOrTestGradingVariables) {
                gradeCommitment = yield getGradeCommitment(identity, gradeGroupOrMerkleProof, gradeCommitmentOrTestGradingVariables.multipleChoiceWeight, gradeCommitmentOrTestGradingVariables.nQuestions);
            }
            else {
                gradeCommitment = gradeCommitmentOrTestGradingVariables;
            }
            gradeMerkleProof = gradeGroupOrMerkleProof.generateMerkleProof(gradeCommitment.gradeCommitmentIndex);
        }
        else {
            if (!("grade" in gradeCommitmentOrTestGradingVariables)) {
                throw new Error("Need to provide the FullGradeCommitment when providing a Merkle proof");
            }
            gradeCommitment = gradeCommitmentOrTestGradingVariables;
            gradeMerkleProof = gradeGroupOrMerkleProof;
        }
        const { proof, publicSignals } = yield groth16.fullProve({
            identityNullifier: identity.nullifier,
            identityTrapdoor: identity.trapdoor,
            gradeTreePathIndices: gradeMerkleProof.pathIndices,
            gradeTreeSiblings: gradeMerkleProof.siblings,
            grade: gradeCommitment.grade,
            gradeThreshold,
            signalHash: hash(signal),
            externalNullifier: hash(externalNullifier)
        }, snarkArtifacts.wasmFilePath, snarkArtifacts.zkeyFilePath);
        return {
            gradeTreeRoot: publicSignals[0],
            nullifierHash: publicSignals[1],
            grade: gradeCommitment.grade,
            gradeThreshold,
            signal,
            externalNullifier,
            proof: packProof(proof)
        };
    });
}

/**
 * Rates a credential issuer by generating a Semaphore proof of credential ownership.
 * @param identity The Semaphore identity.
 * @param groupOrMerkleProof The Semaphore group or its Merkle proof.
 * @param rate The rating given to the credential issuer, from 0 to 100.
 * @param snarkArtifacts The SNARK artifacts.
 * @returns The Semaphore proof ready to be verified.
 */
function generateRateCredentialIssuerProof(identity, groupOrMerkleProof, rating, comment, snarkArtifacts) {
    return __awaiter(this, void 0, void 0, function* () {
        const externalNullifier = formatBytes32String("bq-rate"); // 0x62712d7261746500000000000000000000000000000000000000000000000000
        const abi = new AbiCoder();
        const encodedRating = abi.encode(["uint", "string"], [rating, comment]);
        const signal = BigInt(keccak256(encodedRating));
        if (rating > 100 || rating < 0) {
            throw new Error("Rating value is not supported");
        }
        if (comment.length > MAX_COMMENT_LENGTH) {
            throw new Error("Comment length is too long");
        }
        if (!snarkArtifacts) {
            snarkArtifacts = {
                wasmFilePath: `https://www.trusted-setup-pse.org/semaphore/${MAX_TREE_DEPTH}/semaphore.wasm`,
                zkeyFilePath: `https://www.trusted-setup-pse.org/semaphore/${MAX_TREE_DEPTH}/semaphore.zkey`
            };
        }
        const semaphoreFullProof = yield generateProof(identity, groupOrMerkleProof, externalNullifier, signal, snarkArtifacts);
        return {
            rating,
            comment,
            semaphoreFullProof
        };
    });
}

/**
 * Verifies a Semaphore proof of credential ownership.
 * @param fullProof The SnarkJS Semaphore proof of credential ownership.
 * @returns True if the proof is valid, false otherwise.
 */
function verifyCredentialOwnershipProof(fullProof) {
    return __awaiter(this, void 0, void 0, function* () {
        return verifyProof(fullProof, MAX_TREE_DEPTH);
    });
}

var protocol$1 = "groth16";
var curve$1 = "bn128";
var nPublic$1 = 5;
var vk_alpha_1$1 = [
	"20491192805390485299153009773594534940189261866228447918068658471970481763042",
	"9383485363053290200918347156157836566562967994039712273449902621266178545958",
	"1"
];
var vk_beta_2$1 = [
	[
		"6375614351688725206403948262868962793625744043794305715222011528459656738731",
		"4252822878758300859123897981450591353533073413197771768651442665752259397132"
	],
	[
		"10505242626370262277552901082094356697409835680220590971873171140371331206856",
		"21847035105528745403288232691147584728191162732299865338377159692350059136679"
	],
	[
		"1",
		"0"
	]
];
var vk_gamma_2$1 = [
	[
		"10857046999023057135944570762232829481370756359578518086990519993285655852781",
		"11559732032986387107991004021392285783925812861821192530917403151452391805634"
	],
	[
		"8495653923123431417604973247489272438418190587263600148770280649306958101930",
		"4082367875863433681332203403145435568316851327593401208105741076214120093531"
	],
	[
		"1",
		"0"
	]
];
var vk_delta_2$1 = [
	[
		"728295065907376479109860482420819092339933338424947255962922916389799410790",
		"15910178723148358321363136113167011794832474507169662942681201201555078058358"
	],
	[
		"17464920296666305438909488434645178837149497502495746541808161488102524431655",
		"7784006571998466095853268965264259942918795401698842518913760698714856938458"
	],
	[
		"1",
		"0"
	]
];
var vk_alphabeta_12$1 = [
	[
		[
			"2029413683389138792403550203267699914886160938906632433982220835551125967885",
			"21072700047562757817161031222997517981543347628379360635925549008442030252106"
		],
		[
			"5940354580057074848093997050200682056184807770593307860589430076672439820312",
			"12156638873931618554171829126792193045421052652279363021382169897324752428276"
		],
		[
			"7898200236362823042373859371574133993780991612861777490112507062703164551277",
			"7074218545237549455313236346927434013100842096812539264420499035217050630853"
		]
	],
	[
		[
			"7077479683546002997211712695946002074877511277312570035766170199895071832130",
			"10093483419865920389913245021038182291233451549023025229112148274109565435465"
		],
		[
			"4595479056700221319381530156280926371456704509942304414423590385166031118820",
			"19831328484489333784475432780421641293929726139240675179672856274388269393268"
		],
		[
			"11934129596455521040620786944827826205713621633706285934057045369193958244500",
			"8037395052364110730298837004334506829870972346962140206007064471173334027475"
		]
	]
];
var IC$1 = [
	[
		"7362496364801315793696184153843974232951852611078885917932810756595301318698",
		"21237922218630797152846006696319711665046164136786472140390119679725729865952",
		"1"
	],
	[
		"13160731461514665735531520409871883880619184838963515578483758508212686635370",
		"21863220708116381108581729631571365487158136032768820412867363877305987416056",
		"1"
	],
	[
		"11546573965455705619096759290452233223349603526798277541743654194295066522329",
		"13777640051972569642512234119683083448521223261192914142156221405927871843364",
		"1"
	],
	[
		"1680409684039837936459027540326362303722708692596813423118296130995776129830",
		"2260054383516502283255822072532283655052015967094688986357172706285492267322",
		"1"
	],
	[
		"584044972401202003497156066828404909552959415589458861231385585780127624581",
		"9919599790152630969993358862530562066842214089708433772487077577479760794697",
		"1"
	],
	[
		"11748668666887051675517770518129807684287931148982438320930570818592049508637",
		"16698714620526868189501703908023802811240628598013600714013031099326440255270",
		"1"
	]
];
var verificationKey = {
	protocol: protocol$1,
	curve: curve$1,
	nPublic: nPublic$1,
	vk_alpha_1: vk_alpha_1$1,
	vk_beta_2: vk_beta_2$1,
	vk_gamma_2: vk_gamma_2$1,
	vk_delta_2: vk_delta_2$1,
	vk_alphabeta_12: vk_alphabeta_12$1,
	IC: IC$1
};

/**
 * Verifies a proof where a user claims to have obtained a grade above a certain threshold,
 * @param gradeClaimFullProof The SnarkJS proof of grade claim.
 * @returns True if the proof is valid, false otherwise.
 */
function verifyGradeClaimProof({ gradeTreeRoot, nullifierHash, gradeThreshold, signal, externalNullifier, proof }) {
    return __awaiter(this, void 0, void 0, function* () {
        return groth16.verify(verificationKey, [gradeTreeRoot, nullifierHash, gradeThreshold, hash(signal), hash(externalNullifier)], unpackProof(proof));
    });
}

var protocol = "groth16";
var curve = "bn128";
var nPublic = 10;
var vk_alpha_1 = [
	"20491192805390485299153009773594534940189261866228447918068658471970481763042",
	"9383485363053290200918347156157836566562967994039712273449902621266178545958",
	"1"
];
var vk_beta_2 = [
	[
		"6375614351688725206403948262868962793625744043794305715222011528459656738731",
		"4252822878758300859123897981450591353533073413197771768651442665752259397132"
	],
	[
		"10505242626370262277552901082094356697409835680220590971873171140371331206856",
		"21847035105528745403288232691147584728191162732299865338377159692350059136679"
	],
	[
		"1",
		"0"
	]
];
var vk_gamma_2 = [
	[
		"10857046999023057135944570762232829481370756359578518086990519993285655852781",
		"11559732032986387107991004021392285783925812861821192530917403151452391805634"
	],
	[
		"8495653923123431417604973247489272438418190587263600148770280649306958101930",
		"4082367875863433681332203403145435568316851327593401208105741076214120093531"
	],
	[
		"1",
		"0"
	]
];
var vk_delta_2 = [
	[
		[
			"4540856000900140503523131045700844883347004309759902770531971444702419824351",
			"12942333082934331728993750297006238721468756489273435836235037010830247467967"
		],
		[
			"20949021655794369342840475088782850199693117477619023432532419550901705553271",
			"10713220694634826273056820261569641268368527174836857361685017642659702029653"
		],
		[
			"1",
			"0"
		]
	],
	[
		[
			"20690472726591021362519659193214433501915763012434646907222090111895897337205",
			"13799269636296683296606767401406345079982820292452198697552704257624543012641"
		],
		[
			"21804394630166623203565424646571792889383518626770454011903731655501375857787",
			"15353599038576026736582313087219331610755245504320219019310807930224871707261"
		],
		[
			"1",
			"0"
		]
	],
	[
		[
			"16731607036563878758638686799985828639326992321132815167258641606364873979699",
			"3125629459637059195642800322175047277808135777449084766509794958094752100923"
		],
		[
			"985948279804942928834127099316114301578529641320781073608328905248635586566",
			"21118753855998363853256096909047379452739378294072800652081001947270187627719"
		],
		[
			"1",
			"0"
		]
	]
];
var vk_alphabeta_12 = [
	[
		[
			"2029413683389138792403550203267699914886160938906632433982220835551125967885",
			"21072700047562757817161031222997517981543347628379360635925549008442030252106"
		],
		[
			"5940354580057074848093997050200682056184807770593307860589430076672439820312",
			"12156638873931618554171829126792193045421052652279363021382169897324752428276"
		],
		[
			"7898200236362823042373859371574133993780991612861777490112507062703164551277",
			"7074218545237549455313236346927434013100842096812539264420499035217050630853"
		]
	],
	[
		[
			"7077479683546002997211712695946002074877511277312570035766170199895071832130",
			"10093483419865920389913245021038182291233451549023025229112148274109565435465"
		],
		[
			"4595479056700221319381530156280926371456704509942304414423590385166031118820",
			"19831328484489333784475432780421641293929726139240675179672856274388269393268"
		],
		[
			"11934129596455521040620786944827826205713621633706285934057045369193958244500",
			"8037395052364110730298837004334506829870972346962140206007064471173334027475"
		]
	]
];
var IC = [
	[
		[
			"3460062508552817020486925369254999887263133458813466217731827384934429097020",
			"6548713001085123926575038298695600145312927787528715550119079790931672327813",
			"1"
		],
		[
			"15406797828010507507654709597514439821408759067336276943873336328717012977533",
			"19553210546637991787255561768764488682517242914620423299213539464848415485500",
			"1"
		],
		[
			"19959185561623974415619877157902362479891361197065675787577508710528967104307",
			"13028996927053214837834749241659838061801532423651520178675227615459389210616",
			"1"
		],
		[
			"3310347240584205286564761138090629394369593928734612543936801937448780301664",
			"10900500954734637634564926942755372709002521226615682563984323730454620350391",
			"1"
		],
		[
			"11091680874613960967729951932578059385906300270392786135336684103353733583422",
			"6146308255966051074843231585072122380655579907379922519442391456269011084178",
			"1"
		],
		[
			"12811880879583919745481239883357696952873392785587273972220422768150948251977",
			"15976546320593452163827813166385762914849060167442602352116206563021690783978",
			"1"
		],
		[
			"2917197395052975746065400516817828630265365052212604569191665775907653974177",
			"396250047748014580988740073253604656528377721738973347967828983986117954433",
			"1"
		],
		[
			"12998061194137106909055711176405500564127106907373634397320993374989305381375",
			"7255621895748584856490916992008717489392792541057649710461499089983416377929",
			"1"
		],
		[
			"460068829744378908379382034907278053818690835649865283577316886454704782982",
			"15233131022588598198020254400449240943837285738025101436381461193692321940517",
			"1"
		],
		[
			"12153453805862254887532530442049591861398122148589936265936185544407062280880",
			"3396208004435099672016900113424293135040725911842982531561263577435614395928",
			"1"
		],
		[
			"19165359902719776689239202198859840978181559442556194502339828248740858897084",
			"16819584196418239971003086897134330161552719269824933476869323395879116632712",
			"1"
		]
	],
	[
		[
			"6178464234645025990239182644324979979357561953686286605234926281137177884664",
			"9291658565583776240508675958722285223493046595400506971843052856530415834247",
			"1"
		],
		[
			"20329971974906285278348186615764994642581024425455284387504519443430116790891",
			"142225138864615169879408649544585096057839319602539919981114183901521453136",
			"1"
		],
		[
			"12623171699629737300906378653886128851448034854066489142429041377005317978920",
			"17069080619022065711219923821662655453612150277770462884723837833817492381063",
			"1"
		],
		[
			"9731284393490251946100308731784140959024166737124061902931839160231930966901",
			"20254524038001575515881280820177994623409762212544003383046988676532278498927",
			"1"
		],
		[
			"5035941612547139498310069971733200145167205385240372433350210591192909751799",
			"14951992849208002406655043406058274782529499511717817498586117622041233468138",
			"1"
		],
		[
			"3996862283674922367742079866027143004837562259824041591377740374879907185515",
			"13118379756918589623188825996508305501105823913956921943983349874525657154539",
			"1"
		],
		[
			"10130876028457216435169235410498857105814949264852246030309355622832656762655",
			"14645087973584550144770394515122830035173936873712904656915296407838137638736",
			"1"
		],
		[
			"14497610756517999081256078843398820202264094921488556535798682424184552796020",
			"17452525492691218803120091373460174808503498342569308996167895143023592616795",
			"1"
		],
		[
			"1567474941928983275970670186646466122189820153756155894008676963113496032970",
			"21311685603958880600568626311848420912386517813249054680916234132953994027651",
			"1"
		],
		[
			"8686708691293511211182993765554144148459644581708895011228231954494204359636",
			"2084834714833666699039538063913997335999563691486801901240229861095689880900",
			"1"
		],
		[
			"902184051864523737194207891910179342138897179853813433530804819669953673391",
			"17737121108843234527813362294770298533844024043256228414988320185580411027802",
			"1"
		]
	],
	[
		[
			"15701663590014921532921960813024076515886450481041981352973071644628957473885",
			"4029702578219577355115054213159511197918112798336489277312432195437629346817",
			"1"
		],
		[
			"11411298893507778768336411773838968844659532549412067564558799462375959797095",
			"10883970276499694761788988875416554326109978475435934452151304707463117068975",
			"1"
		],
		[
			"1657441127695113463529209762911849146841225886309275570893822196612029371732",
			"10874417601615041302352521596020387512033194820636514702063861556248080825588",
			"1"
		],
		[
			"17607850747391341893026091408980065582429471212100689784395431754124259109194",
			"16688303949459255693399421892987011114075359691366181809552376455687109369446",
			"1"
		],
		[
			"11572082814276390223512472734823186365819913031800277669751535038606453949292",
			"21525510712377249581951761853570902634635069971403517775817444904352008555342",
			"1"
		],
		[
			"21204928170726277593552043219174579204073686950798675036234612605037073736476",
			"2659881797694149387503429734998373655623868181360190277682447753253710279594",
			"1"
		],
		[
			"10772037888198166136018674235480352098674793982056761284820438114241261609126",
			"20182667549757899077322548004901568873857241075530987939066314357479282993026",
			"1"
		],
		[
			"12337706999080323854295315910607030000381520032870032279255049676917325863519",
			"6972646636366142138679204120770062855354552378861769746846311381920140415565",
			"1"
		],
		[
			"18940299556498154953678453716188790194410692603814034563696314566209848083753",
			"7803244552057865178378377026699298263752062733835846028274845474083602953132",
			"1"
		],
		[
			"17088593136992153437302524853047591525887337200256155839884102126300941605365",
			"1803668846264618363435548357436383698719943509141728457626220829104269687018",
			"1"
		],
		[
			"17738932611525213992745645311482081757330800695433582791577884618493521158673",
			"6032819592984899501611611506264574089620242223369852548605797584620768352928",
			"1"
		]
	]
];
var testKeys = {
	protocol: protocol,
	curve: curve,
	nPublic: nPublic,
	vk_alpha_1: vk_alpha_1,
	vk_beta_2: vk_beta_2,
	vk_gamma_2: vk_gamma_2,
	vk_delta_2: vk_delta_2,
	vk_alphabeta_12: vk_alphabeta_12,
	IC: IC
};

/**
 * Verifies a a proof of knowledge of a solution to a Block Qualified test.
 * @param fullProof The SnarkJS proof of knowledge.
 * @returns True if the proof is valid, false otherwise.
 */
function verifyTestProof({ publicSignals, proof }, testHeight) {
    if (!SUPPORTED_TEST_HEIGHTS.includes(testHeight)) {
        throw new TypeError("The test height given is not supported");
    }
    const verificationKey = Object.assign(Object.assign({}, testKeys), { vk_delta_2: testKeys.vk_delta_2[testHeight - 4], IC: testKeys.IC[testHeight - 4] });
    return groth16.verify(verificationKey, publicSignals, unpackProof(proof));
}

var CredentialRegistryABI = [
	{
		inputs: [
			{
				internalType: "address",
				name: "semaphoreVerifierAddress",
				type: "address"
			},
			{
				internalType: "address",
				name: "gradeClaimVerifierAddress",
				type: "address"
			}
		],
		stateMutability: "nonpayable",
		type: "constructor"
	},
	{
		inputs: [
		],
		name: "CredentialDoesNotExist",
		type: "error"
	},
	{
		inputs: [
		],
		name: "CredentialIdAlreadyExists",
		type: "error"
	},
	{
		inputs: [
		],
		name: "CredentialTypeAlreadyDefined",
		type: "error"
	},
	{
		inputs: [
		],
		name: "CredentialTypeDoesNotExist",
		type: "error"
	},
	{
		inputs: [
		],
		name: "InvalidCredentialManagerAddress",
		type: "error"
	},
	{
		inputs: [
		],
		name: "InvalidRating",
		type: "error"
	},
	{
		inputs: [
		],
		name: "InvalidTreeDepth",
		type: "error"
	},
	{
		inputs: [
		],
		name: "MerkleTreeRootIsExpired",
		type: "error"
	},
	{
		inputs: [
		],
		name: "MerkleTreeRootIsNotPartOfTheGroup",
		type: "error"
	},
	{
		inputs: [
		],
		name: "Semaphore__GroupAlreadyExists",
		type: "error"
	},
	{
		inputs: [
		],
		name: "Semaphore__GroupDoesNotExist",
		type: "error"
	},
	{
		inputs: [
		],
		name: "UsingSameNullifierTwice",
		type: "error"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				indexed: true,
				internalType: "uint256",
				name: "credentialType",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "merkleTreeDepth",
				type: "uint256"
			}
		],
		name: "CredentialCreated",
		type: "event"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "merkleTreeDepth",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "zeroValue",
				type: "uint256"
			}
		],
		name: "GroupCreated",
		type: "event"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "index",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "identityCommitment",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "merkleTreeRoot",
				type: "uint256"
			}
		],
		name: "MemberAdded",
		type: "event"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "index",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "identityCommitment",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "merkleTreeRoot",
				type: "uint256"
			}
		],
		name: "MemberRemoved",
		type: "event"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "index",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "identityCommitment",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "newIdentityCommitment",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "merkleTreeRoot",
				type: "uint256"
			}
		],
		name: "MemberUpdated",
		type: "event"
	},
	{
		anonymous: false,
		inputs: [
			{
				indexed: true,
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				indexed: true,
				internalType: "address",
				name: "admin",
				type: "address"
			},
			{
				indexed: false,
				internalType: "uint256",
				name: "rating",
				type: "uint256"
			},
			{
				indexed: false,
				internalType: "string",
				name: "comment",
				type: "string"
			}
		],
		name: "NewCredentialRating",
		type: "event"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "treeDepth",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "credentialType",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "merkleTreeDuration",
				type: "uint256"
			},
			{
				internalType: "bytes",
				name: "credentialData",
				type: "bytes"
			},
			{
				internalType: "string",
				name: "credentialURI",
				type: "string"
			}
		],
		name: "createCredential",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "credentialExists",
		outputs: [
			{
				internalType: "bool",
				name: "",
				type: "bool"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "credentialIsValid",
		outputs: [
			{
				internalType: "bool",
				name: "",
				type: "bool"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		name: "credentialManagers",
		outputs: [
			{
				internalType: "address",
				name: "",
				type: "address"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		name: "credentialParameters",
		outputs: [
			{
				internalType: "uint256",
				name: "treeDepth",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "credentialType",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "merkleTreeDuration",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		name: "credentialRatings",
		outputs: [
			{
				internalType: "uint128",
				name: "totalRating",
				type: "uint128"
			},
			{
				internalType: "uint128",
				name: "nRatings",
				type: "uint128"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		name: "credentialStates",
		outputs: [
			{
				internalType: "uint80",
				name: "gradeTreeIndex",
				type: "uint80"
			},
			{
				internalType: "uint80",
				name: "credentialsTreeIndex",
				type: "uint80"
			},
			{
				internalType: "uint80",
				name: "noCredentialsTreeIndex",
				type: "uint80"
			},
			{
				internalType: "uint256",
				name: "gradeTreeRoot",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "credentialsTreeRoot",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "noCredentialsTreeRoot",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		name: "credentialURIs",
		outputs: [
			{
				internalType: "string",
				name: "",
				type: "string"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialType",
				type: "uint256"
			},
			{
				internalType: "address",
				name: "credentialManager",
				type: "address"
			}
		],
		name: "defineCredentialType",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialAdmin",
		outputs: [
			{
				internalType: "address",
				name: "",
				type: "address"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialAverageRating",
		outputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialData",
		outputs: [
			{
				internalType: "bytes",
				name: "",
				type: "bytes"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialManager",
		outputs: [
			{
				internalType: "address",
				name: "",
				type: "address"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialType",
		outputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "getCredentialURI",
		outputs: [
			{
				internalType: "string",
				name: "",
				type: "string"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "merkleRoot",
				type: "uint256"
			}
		],
		name: "getMerkleRootCreationDate",
		outputs: [
			{
				internalType: "uint256",
				name: "creationDate",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			}
		],
		name: "getMerkleTreeDepth",
		outputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			}
		],
		name: "getMerkleTreeRoot",
		outputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "groupId",
				type: "uint256"
			}
		],
		name: "getNumberOfMerkleTreeLeaves",
		outputs: [
			{
				internalType: "uint256",
				name: "",
				type: "uint256"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
		],
		name: "gradeClaimVerifier",
		outputs: [
			{
				internalType: "contract IGradeClaimVerifier",
				name: "",
				type: "address"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			}
		],
		name: "invalidateCredential",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "credentialsTreeRoot",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "nullifierHash",
				type: "uint256"
			},
			{
				internalType: "uint256[8]",
				name: "proof",
				type: "uint256[8]"
			},
			{
				internalType: "uint128",
				name: "rating",
				type: "uint128"
			},
			{
				internalType: "string",
				name: "comment",
				type: "string"
			}
		],
		name: "rateCredential",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
		],
		name: "semaphoreVerifier",
		outputs: [
			{
				internalType: "contract SemaphoreVerifier",
				name: "",
				type: "address"
			}
		],
		stateMutability: "view",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "bytes",
				name: "credentialUpdate",
				type: "bytes"
			}
		],
		name: "updateCredential",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "merkleTreeRoot",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "nullifierHash",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "signal",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "externalNullifier",
				type: "uint256"
			},
			{
				internalType: "uint256[8]",
				name: "proof",
				type: "uint256[8]"
			}
		],
		name: "verifyCredentialOwnershipProof",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "gradeTreeRoot",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "nullifierHash",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "gradeThreshold",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "signal",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "externalNullifier",
				type: "uint256"
			},
			{
				internalType: "uint256[8]",
				name: "proof",
				type: "uint256[8]"
			}
		],
		name: "verifyGradeClaimProof",
		outputs: [
		],
		stateMutability: "nonpayable",
		type: "function"
	},
	{
		inputs: [
			{
				internalType: "uint256",
				name: "credentialId",
				type: "uint256"
			},
			{
				internalType: "uint256",
				name: "nullifierHash",
				type: "uint256"
			}
		],
		name: "wasNullifierHashUsed",
		outputs: [
			{
				internalType: "bool",
				name: "",
				type: "bool"
			}
		],
		stateMutability: "view",
		type: "function"
	}
];

var _LegacyCredential_poseidon, _LegacyCredential_credentialId, _LegacyCredential_credentialsRegistry, _LegacyCredential_minimumGrade, _LegacyCredential_gradeGroup, _LegacyCredential_credentialsGroup, _LegacyCredential_noCredentialsGroup;
class LegacyCredential {
    // Defines the test via its testId and the smart contract that governs it
    // @param testId Test ID of the test we are focused on.
    // @param chainId Chain ID of the network where this bqTest is defined.
    // @param credentialsContractAddress Address of the Credentials smart contract for this chainId.
    constructor(poseidon, credentialId, credentialsRegistry, minimumGrade, gradeGroup, credentialsGroup, noCredentialsGroup) {
        _LegacyCredential_poseidon.set(this, void 0);
        _LegacyCredential_credentialId.set(this, void 0);
        _LegacyCredential_credentialsRegistry.set(this, void 0);
        _LegacyCredential_minimumGrade.set(this, void 0);
        _LegacyCredential_gradeGroup.set(this, void 0);
        _LegacyCredential_credentialsGroup.set(this, void 0);
        _LegacyCredential_noCredentialsGroup.set(this, void 0);
        __classPrivateFieldSet(this, _LegacyCredential_poseidon, poseidon, "f");
        __classPrivateFieldSet(this, _LegacyCredential_credentialId, credentialId, "f");
        __classPrivateFieldSet(this, _LegacyCredential_credentialsRegistry, credentialsRegistry, "f");
        __classPrivateFieldSet(this, _LegacyCredential_minimumGrade, minimumGrade, "f");
        __classPrivateFieldSet(this, _LegacyCredential_gradeGroup, gradeGroup, "f");
        __classPrivateFieldSet(this, _LegacyCredential_credentialsGroup, credentialsGroup, "f");
        __classPrivateFieldSet(this, _LegacyCredential_noCredentialsGroup, noCredentialsGroup, "f");
    }
    // Spins up a new Legacy Credential
    static new(credentialId, treeDepth, merkleTreeDuration, credentialURI, minimumGrade, legacyCredentialRecipients, signer, options = {}, networkOrEthereumURL = "maticmum") {
        return __awaiter(this, void 0, void 0, function* () {
            let poseidon = yield buildPoseidon();
            switch (networkOrEthereumURL) {
                case "maticmum":
                    options.credentialsRegistryAddress = DEPLOYED_CONTRACTS.maticmum.credentialsRegistryAddress;
                    options.legacyCredentialType = DEPLOYED_CONTRACTS.maticmum.testCredentialType;
                    break;
                default:
                    if (options.credentialsRegistryAddress === undefined) {
                        throw new Error(`You should provide the Credentials Registry contract address for this network`);
                    }
                    if (options.legacyCredentialType === undefined) {
                        options.legacyCredentialType = 1;
                    }
            }
            const credentialsRegistry = new Contract(options.credentialsRegistryAddress, CredentialRegistryABI, signer);
            const credentialExists = yield credentialsRegistry.credentialExists(credentialId);
            if (credentialExists) {
                throw new Error(`Credential #${credentialId} already exists`);
            }
            let gradeGroupMembers = [];
            let credentialsGroupMembers = [];
            let noCredentialsGroupMembers = [];
            legacyCredentialRecipients.forEach((recipient) => {
                const { trapdoor, nullifier, commitment } = new Identity(recipient.userSecret);
                gradeGroupMembers.push(poseidon([poseidon([nullifier, trapdoor]), recipient.grade]).toString());
                if (recipient.grade >= minimumGrade) {
                    credentialsGroupMembers.push(commitment.toString());
                }
                else {
                    noCredentialsGroupMembers.push(commitment.toString());
                }
            });
            let gradeGroup = new Group(credentialId);
            let credentialsGroup = new Group(credentialId);
            let noCredentialsGroup = new Group(credentialId);
            gradeGroup.addMembers(gradeGroupMembers);
            credentialsGroup.addMembers(credentialsGroupMembers);
            noCredentialsGroup.addMembers(noCredentialsGroupMembers);
            const credentialData = encodeLegacyCredential(gradeGroupMembers.length, credentialsGroupMembers.length, noCredentialsGroupMembers.length, gradeGroup.root.toString(), credentialsGroup.root.toString(), noCredentialsGroup.root.toString(), minimumGrade);
            // Create the new credential on-chain
            const tx = yield credentialsRegistry.createCredential(credentialId, treeDepth, options.legacyCredentialType, merkleTreeDuration, credentialData, credentialURI);
            yield tx.wait();
            return new LegacyCredential(poseidon, credentialId, credentialsRegistry, minimumGrade, gradeGroup, credentialsGroup, noCredentialsGroup);
        });
    }
    // Loads a Legacy Credential
    static load(credentialId, gradeGroup, credentialsGroup, noCredentialsGroup, signer, options = {}, networkOrEthereumURL = "maticmum") {
        return __awaiter(this, void 0, void 0, function* () {
            let poseidon = yield buildPoseidon();
            switch (networkOrEthereumURL) {
                case "maticmum":
                    options.credentialsRegistryAddress = DEPLOYED_CONTRACTS.maticmum.credentialsRegistryAddress;
                    options.legacyCredentialType = DEPLOYED_CONTRACTS.maticmum.testCredentialType;
                    break;
                default:
                    if (options.credentialsRegistryAddress === undefined) {
                        throw new Error(`You should provide the Credentials Registry contract address for this network`);
                    }
                    if (options.legacyCredentialType === undefined) {
                        options.legacyCredentialType = 1;
                    }
            }
            const credentialsRegistry = new Contract(options.credentialsRegistryAddress, CredentialRegistryABI, signer);
            let credentialType;
            try {
                credentialType = yield credentialsRegistry.getCredentialType(credentialId);
            }
            catch (_) {
                throw new Error(`Credential #${credentialId} does not exist`);
            }
            if (options.legacyCredentialType !== credentialType.toNumber()) {
                throw new Error(`Credential #${credentialId} is not a Legacy Credential`);
            }
            const credentialAdmin = yield credentialsRegistry.getCredentialAdmin(credentialId);
            const signerAddress = yield signer.getAddress();
            if (signerAddress !== credentialAdmin) {
                throw new Error(`Signer provided is not the credential admin ${credentialAdmin}`);
            }
            const credentialData = decodeLegacyCredentialData(yield credentialsRegistry.getCredentialData(credentialId));
            // Verify that the groups given correspond to those on-chain
            // Don't really need to check that the number of leaves is correct, as it gets encoded in the root
            if (gradeGroup.root.toString() !== credentialData.credentialState.gradeTreeRoot) {
                throw new Error("Grade group provided does not match with the on-chain root");
            }
            if (credentialsGroup.root.toString() !== credentialData.credentialState.credentialsTreeRoot) {
                throw new Error("Credentials group provided does not match with the on-chain root");
            }
            if (noCredentialsGroup.root.toString() !== credentialData.credentialState.noCredentialsTreeRoot) {
                throw new Error("No-credentials group provided does not match with the on-chain root");
            }
            return new LegacyCredential(poseidon, credentialId, credentialsRegistry, credentialData.minimumGrade, gradeGroup, credentialsGroup, noCredentialsGroup);
        });
    }
    // updates the given user secret to an actual identity commitment
    setNewUserIdentity(legacyCredentialRecipient, newIdentity) {
        const oldIdentity = new Identity(legacyCredentialRecipient.userSecret);
        const gradeIndex = __classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").indexOf(__classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [__classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [oldIdentity.nullifier, oldIdentity.trapdoor]), legacyCredentialRecipient.grade]));
        if (gradeIndex === -1) {
            throw new Error("Recipient was not found");
        }
        __classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").updateMember(gradeIndex, __classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [__classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [newIdentity.nullifier, newIdentity.trapdoor]), legacyCredentialRecipient.grade]));
        if (legacyCredentialRecipient.grade >= __classPrivateFieldGet(this, _LegacyCredential_minimumGrade, "f")) {
            const credentialIndex = __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").indexOf(oldIdentity.commitment);
            __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").updateMember(credentialIndex, newIdentity.commitment);
        }
        else {
            const noCredentialsIndex = __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").indexOf(oldIdentity.commitment);
            __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").updateMember(noCredentialsIndex, newIdentity.commitment);
        }
    }
    addCredentialRecipient(legacyCredentialRecipient) {
        const { trapdoor, nullifier, commitment } = new Identity(legacyCredentialRecipient.userSecret);
        __classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").addMember(__classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [__classPrivateFieldGet(this, _LegacyCredential_poseidon, "f").call(this, [nullifier, trapdoor]), legacyCredentialRecipient.grade]).toString());
        if (legacyCredentialRecipient.grade >= __classPrivateFieldGet(this, _LegacyCredential_minimumGrade, "f")) {
            __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").addMember(commitment.toString());
        }
        else {
            __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").addMember(commitment.toString());
        }
    }
    publishChanges() {
        return __awaiter(this, void 0, void 0, function* () {
            const currentData = decodeLegacyCredentialData(yield __classPrivateFieldGet(this, _LegacyCredential_credentialsRegistry, "f").getCredentialData(__classPrivateFieldGet(this, _LegacyCredential_credentialId, "f")));
            if (__classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").root.toString() === currentData.credentialState.gradeTreeRoot &&
                __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").root.toString() === currentData.credentialState.credentialsTreeRoot &&
                __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").root.toString() === currentData.credentialState.noCredentialsTreeRoot) {
                throw new Error("Cannot publish changes as no changes were made");
            }
            const credentialUpdate = encodeLegacyCredential(__classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").members.length, __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").members.length, __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").members.length, __classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f").root.toString(), __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f").root.toString(), __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f").root.toString());
            // Publish changes on-chain
            const tx = yield __classPrivateFieldGet(this, _LegacyCredential_credentialsRegistry, "f").updateCredential(__classPrivateFieldGet(this, _LegacyCredential_credentialId, "f"), credentialUpdate);
            yield tx.wait();
        });
    }
    get gradeGroup() {
        return __classPrivateFieldGet(this, _LegacyCredential_gradeGroup, "f");
    }
    get credentialsGroup() {
        return __classPrivateFieldGet(this, _LegacyCredential_credentialsGroup, "f");
    }
    get noCredentialsGroup() {
        return __classPrivateFieldGet(this, _LegacyCredential_noCredentialsGroup, "f");
    }
    URI() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield __classPrivateFieldGet(this, _LegacyCredential_credentialsRegistry, "f").getCredentialURI(__classPrivateFieldGet(this, _LegacyCredential_credentialId, "f"));
        });
    }
    averageRating() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield __classPrivateFieldGet(this, _LegacyCredential_credentialsRegistry, "f").getCredentialAverageRating(__classPrivateFieldGet(this, _LegacyCredential_credentialId, "f"))).toNumber();
        });
    }
}
_LegacyCredential_poseidon = new WeakMap(), _LegacyCredential_credentialId = new WeakMap(), _LegacyCredential_credentialsRegistry = new WeakMap(), _LegacyCredential_minimumGrade = new WeakMap(), _LegacyCredential_gradeGroup = new WeakMap(), _LegacyCredential_credentialsGroup = new WeakMap(), _LegacyCredential_noCredentialsGroup = new WeakMap();

/**
 * Check if the parameter type is correct.
 * @param value Parameter value.
 * @param name Parameter name.
 * @param type Expected parameter type.
 */
function checkParameter(value, name, type) {
    if (typeof value !== type) {
        throw new TypeError(`Parameter '${name}' is not ${type === "object" ? "an" : "a"} ${type}`);
    }
}

/**
 * Returns the subgraph URL related to the network passed as a parameter.
 * @param network Semaphore supported network.
 * @returns Subgraph URL.
 */
function getURL(network) {
    switch (network) {
        case "maticmum":
            return `https://api.thegraph.com/subgraphs/name/0xdeenz/blockqualified`;
        default:
            throw new TypeError(`Network '${network}' is not supported`);
    }
}

/**
 * Returns the response data of an HTTP request.
 * @param url HTTP URL.
 * @param config Axios request configuration.
 * @returns Request data.
 */
/* istanbul ignore next */
function request(url, config) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data } = yield axios(url, Object.assign({ headers: Object.assign({ "Content-Type": "application/json" }, config === null || config === void 0 ? void 0 : config.headers) }, config));
        return data === null || data === void 0 ? void 0 : data.data;
    });
}

class BlockQualifiedSubgraph {
    /**
     * Initializes the subgraph object with one of the supported networks or a custom URL.
     * @param networkOrSubgraphURL Supported Semaphore network or custom Subgraph URL.
     */
    constructor(networkOrSubgraphURL = "maticmum") {
        checkParameter(networkOrSubgraphURL, "networkOrSubgraphURL", "string");
        if (networkOrSubgraphURL.startsWith("http")) {
            this._url = networkOrSubgraphURL;
            return;
        }
        this._url = getURL(networkOrSubgraphURL);
    }
    /**
     * Returns the subgraph URL.
     * @returns Subgraph URL.
     */
    get url() {
        return this._url;
    }
    /**
     * Returns a specific group.
     * @param groupId Group id.
     * @param group Options to select the group parameters.
     * @returns Specific group.
     */
    getGroupMembers(credentialId, group) {
        return __awaiter(this, void 0, void 0, function* () {
            checkParameter(credentialId, "credentialId", "number");
            let groupId;
            switch (group) {
                case "grade":
                    groupId = (3 * (credentialId - 1) + 1).toString();
                    break;
                case "credentials":
                    groupId = (3 * (credentialId - 1) + 2).toString();
                    break;
                case "no-credentials":
                    groupId = (3 * (credentialId - 1) + 3).toString();
                    break;
                default:
                    throw new TypeError(`Group '${group}' is not valid`);
            }
            const config = {
                method: "post",
                data: JSON.stringify({
                    query: `{
                    groups(where: { id: "${groupId}" }) {
                        id
                        merkleTree {
                            root
                            depth
                            numberOfLeaves
                        }
                        members(orderBy: index) {
                            identityCommitment
                        }
                    }
                }`
                })
            };
            const { groups } = yield request(this._url, config);
            return groups[0].members.map((member) => member.identityCommitment);
        });
    }
}

var _TestCredential_instances, _TestCredential_poseidon, _TestCredential_subgraph, _TestCredential_network, _TestCredential_credentialId, _TestCredential_testData, _TestCredential_credentialsRegistry, _TestCredential_treeDepth, _TestCredential_openAnswersHashes, _TestCredential_autotaskWebhook, _TestCredential_getMultipleChoiceResult, _TestCredential_getOpenAnswersResult;
class TestCredential {
    // Defines the test via its testId and the smart contract that governs it
    // @param testId Test ID of the test we are focused on.
    // @param chainId Chain ID of the network where this bqTest is defined.
    // @param credentialsContractAddress Address of the Credentials smart contract for this chainId.
    constructor(poseidon, blockQualifiedSubgraph, credentialId, network, testData, credentialsRegistry, treeDepth, autotaskWebhook, openAnswersHashes) {
        _TestCredential_instances.add(this);
        _TestCredential_poseidon.set(this, void 0);
        _TestCredential_subgraph.set(this, void 0);
        _TestCredential_network.set(this, void 0);
        _TestCredential_credentialId.set(this, void 0);
        _TestCredential_testData.set(this, void 0);
        _TestCredential_credentialsRegistry.set(this, void 0);
        _TestCredential_treeDepth.set(this, void 0);
        _TestCredential_openAnswersHashes.set(this, void 0);
        _TestCredential_autotaskWebhook.set(this, void 0);
        _TestCredential_getOpenAnswersResult.set(this, ({ openAnswers }) => {
            let nCorrect = 0;
            const resultsArray = new Array(openAnswers.length).fill(false);
            for (var i = 0; i < Math.pow(2, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight); i++) {
                if (i < openAnswers.length) {
                    if (__classPrivateFieldGet(this, _TestCredential_poseidon, "f").call(this, [hash(openAnswers[i])]).toString() === __classPrivateFieldGet(this, _TestCredential_openAnswersHashes, "f")[i]) {
                        nCorrect++;
                        resultsArray[i] = true;
                    }
                }
                else {
                    nCorrect++; // Default hash is always correct, simply filling to 2 ** testHeight
                }
            }
            const openAnswersResult = (nCorrect + __classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions > Math.pow(2, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight)) ? // prevent underflow
                Math.floor((100 - __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight) *
                    (nCorrect + __classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions - Math.pow(2, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight)) / __classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions)
                :
                    0;
            return {
                nCorrect,
                resultsArray,
                openAnswersResult
            };
        }
        // TODO: some other function to get procedurally more rating comments, from latest to earliest or opposite
        // TODO: functions to generate/verify ownership/grade claim proofs, and see if a nullifier was already used
        );
        __classPrivateFieldSet(this, _TestCredential_poseidon, poseidon, "f");
        __classPrivateFieldSet(this, _TestCredential_subgraph, blockQualifiedSubgraph, "f");
        __classPrivateFieldSet(this, _TestCredential_credentialId, credentialId, "f");
        __classPrivateFieldSet(this, _TestCredential_network, network, "f");
        __classPrivateFieldSet(this, _TestCredential_testData, testData, "f");
        __classPrivateFieldSet(this, _TestCredential_credentialsRegistry, credentialsRegistry, "f");
        __classPrivateFieldSet(this, _TestCredential_treeDepth, treeDepth, "f");
        __classPrivateFieldSet(this, _TestCredential_autotaskWebhook, autotaskWebhook, "f");
        __classPrivateFieldSet(this, _TestCredential_openAnswersHashes, openAnswersHashes !== null && openAnswersHashes !== void 0 ? openAnswersHashes : [], "f");
    }
    static init(credentialId, options = {}, networkOrEthereumURL = "maticmum", openAnswersHashes) {
        return __awaiter(this, void 0, void 0, function* () {
            let poseidon = yield buildPoseidon();
            let blockQualifiedSubgraph = new BlockQualifiedSubgraph(networkOrEthereumURL);
            switch (networkOrEthereumURL) {
                case "maticmum":
                    options.credentialsRegistryAddress = DEPLOYED_CONTRACTS.maticmum.credentialsRegistryAddress;
                    options.testCredentialType = DEPLOYED_CONTRACTS.maticmum.testCredentialType;
                    options.autotaskWebhook = DEPLOYED_CONTRACTS.maticmum.autotaskWebhook;
                    break;
                default:
                    if (options.credentialsRegistryAddress === undefined) {
                        throw new Error(`You should provide the Credentials Registry contract address for this network`);
                    }
                    if (options.testCredentialType === undefined) {
                        options.testCredentialType = 0;
                    }
                    options.autotaskWebhook = "";
            }
            let provider;
            switch (options.provider) {
                case "infura":
                    provider = new InfuraProvider(networkOrEthereumURL, options.apiKey);
                    break;
                case "alchemy":
                    provider = new AlchemyProvider(networkOrEthereumURL, options.apiKey);
                    break;
                case "cloudflare":
                    provider = new CloudflareProvider(networkOrEthereumURL, options.apiKey);
                    break;
                case "etherscan":
                    provider = new EtherscanProvider(networkOrEthereumURL, options.apiKey);
                    break;
                case "pocket":
                    provider = new PocketProvider(networkOrEthereumURL, options.apiKey);
                    break;
                case "ankr":
                    provider = new AnkrProvider(networkOrEthereumURL, options.apiKey);
                    break;
                default:
                    if (!networkOrEthereumURL.startsWith("http")) {
                        throw new Error(`Provider '${options.provider}' is not supported`);
                    }
                    provider = new JsonRpcProvider(networkOrEthereumURL);
            }
            const credentialsRegistry = new Contract(options.credentialsRegistryAddress, CredentialRegistryABI, provider);
            const credentialType = yield credentialsRegistry.getCredentialType(credentialId);
            if (options.testCredentialType !== credentialType.toNumber()) {
                throw new Error(`Credential #${credentialId} is not a Test Credential`);
            }
            const testData = decodeTestCredentialData(yield credentialsRegistry.getCredentialData(credentialId));
            let fullOpenAnswersHashes = new Array(Math.pow(2, testData.testHeight)).fill(poseidon([hash("")]));
            if (testData.multipleChoiceWeight !== 100) { // Open answer hashes need to be provided
                if (openAnswersHashes === undefined) {
                    throw new Error(`Open answers hashes need to be provided for tests that are not multiple choice`);
                }
                if (openAnswersHashes.length !== testData.nQuestions) {
                    throw new Error(`Need to provide an open answer hash for each question`);
                }
                fullOpenAnswersHashes.forEach((_, i) => {
                    if (i < openAnswersHashes.length) {
                        fullOpenAnswersHashes[i] = openAnswersHashes[i].toString();
                    }
                });
            }
            const treeDepth = (yield credentialsRegistry.getMerkleTreeDepth(3 * credentialId)).toNumber();
            return new TestCredential(poseidon, blockQualifiedSubgraph, credentialId, networkOrEthereumURL, testData, credentialsRegistry, treeDepth, options.autotaskWebhook, fullOpenAnswersHashes);
        });
    }
    gradeSolution(testAnswers) {
        // Multiple choice answers provided must be numbers and less than 2 ** testHeight in number
        if (testAnswers.multipleChoiceAnswers.length > Math.pow(2, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight)) {
            throw new RangeError('Surpassed maximum number of answers for a test');
        }
        // All open answers must be provided - even if an empty ""
        if (__classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight !== 100 && testAnswers.openAnswers.length < __classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions) {
            throw new RangeError('Some questions were left unanswered');
        }
        if (testAnswers.openAnswers.length > __classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions) {
            throw new RangeError(`Answered ${testAnswers.openAnswers.length} questions while the test only has ${__classPrivateFieldGet(this, _TestCredential_testData, "f").nQuestions}`);
        }
        // Multiple choice component
        const multipleChoiceResult = __classPrivateFieldGet(this, _TestCredential_instances, "m", _TestCredential_getMultipleChoiceResult).call(this, testAnswers);
        // Open answer component
        const { resultsArray, openAnswersResult } = __classPrivateFieldGet(this, _TestCredential_getOpenAnswersResult, "f").call(this, testAnswers);
        const testResult = multipleChoiceResult + openAnswersResult;
        return {
            grade: testResult,
            minimumGrade: __classPrivateFieldGet(this, _TestCredential_testData, "f").minimumGrade,
            pass: testResult >= __classPrivateFieldGet(this, _TestCredential_testData, "f").minimumGrade,
            nQuestions: testAnswers.openAnswers.length,
            multipleChoiceGrade: __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight === 0 ? 0 : 100 * multipleChoiceResult / __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight,
            openAnswerGrade: __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight === 100 ? 0 : 100 * openAnswersResult / (100 - __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight),
            multipleChoiceWeight: __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight,
            openAnswerResults: resultsArray,
        };
    }
    generateSolutionProof(identity, testAnswers, testSnarkArtifacts, semaphoreSnarkArtifacts, gradeClaimSnarkArtifacts) {
        return __awaiter(this, void 0, void 0, function* () {
            const grade = this.gradeSolution(testAnswers);
            testAnswers.multipleChoiceAnswers = generateMultipleChoiceAnswers(testAnswers.multipleChoiceAnswers, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight);
            testAnswers.openAnswers = generateOpenAnswers(testAnswers.openAnswers, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight);
            const identityGroup = new Group(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), __classPrivateFieldGet(this, _TestCredential_treeDepth, "f"));
            const gradeGroup = new Group(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), __classPrivateFieldGet(this, _TestCredential_treeDepth, "f"));
            const testVariables = Object.assign(Object.assign({}, __classPrivateFieldGet(this, _TestCredential_testData, "f")), { minimumGrade: grade.pass ? __classPrivateFieldGet(this, _TestCredential_testData, "f").minimumGrade : 0, openAnswersHashes: __classPrivateFieldGet(this, _TestCredential_openAnswersHashes, "f") });
            const gradeGroupMembers = yield __classPrivateFieldGet(this, _TestCredential_subgraph, "f").getGroupMembers(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), "grade");
            gradeGroup.addMembers(gradeGroupMembers);
            const identityGroupMembers = yield __classPrivateFieldGet(this, _TestCredential_subgraph, "f").getGroupMembers(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), grade.pass ? "credentials" : "no-credentials");
            identityGroup.addMembers(identityGroupMembers);
            if (__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredentialGradeThreshold !== 0) { // Grade restricted test
                const treeDepth = (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getMerkleTreeDepth(3 * __classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential)).toNumber();
                const requiredCredentialsGradeGroup = new Group(__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential, treeDepth);
                requiredCredentialsGradeGroup.addMembers(yield __classPrivateFieldGet(this, _TestCredential_subgraph, "f").getGroupMembers(__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential, "grade"));
                const testData = decodeTestCredentialData(yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getCredentialData(__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential));
                return generateGradeRestrictedTestProof(identity, testAnswers, testVariables, identityGroup, gradeGroup, requiredCredentialsGradeGroup, __classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredentialGradeThreshold, { multipleChoiceWeight: testData.multipleChoiceWeight, nQuestions: testData.nQuestions }, grade.pass, testSnarkArtifacts, gradeClaimSnarkArtifacts);
            }
            else if (__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential !== 0) { // Credential restricted test
                const treeDepth = (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getMerkleTreeDepth(3 * __classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential)).toNumber();
                const requiredCredentialsGroup = new Group(__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential, treeDepth);
                requiredCredentialsGroup.addMembers(yield __classPrivateFieldGet(this, _TestCredential_subgraph, "f").getGroupMembers(__classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential, "credentials"));
                return generateCredentialRestrictedTestProof(identity, testAnswers, testVariables, identityGroup, gradeGroup, requiredCredentialsGroup, grade.pass, testSnarkArtifacts, semaphoreSnarkArtifacts);
            }
            else { // Open test
                return generateTestProof(identity, testAnswers, testVariables, identityGroup, gradeGroup, grade.pass, testSnarkArtifacts);
            }
        });
    }
    verifySolutionProof(proof) {
        return __awaiter(this, void 0, void 0, function* () {
            if ("gradeClaimFullProof" in proof) { // Grade restricted test
                return (yield verifyGradeClaimProof(proof.gradeClaimFullProof)) && (yield verifyTestProof(proof.testFullProof, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight));
            }
            else if ("semaphoreFullProof" in proof) { // Credential restricted test
                return (yield verifyCredentialOwnershipProof(proof.semaphoreFullProof)) && (yield verifyTestProof(proof.testFullProof, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight));
            }
            else { // Open test
                return verifyTestProof(proof, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight);
            }
        });
    }
    sendSolutionTransaction(proof) {
        return __awaiter(this, void 0, void 0, function* () {
            let credentialUpdate;
            if ("gradeClaimFullProof" in proof) { // Grade restricted test
                credentialUpdate = encodeGradeRestrictedTestFullProof(proof);
            }
            else if ("semaphoreFullProof" in proof) { // Credential restricted test
                credentialUpdate = encodeCredentialRestrictedTestFullProof(proof);
            }
            else { // Open test
                credentialUpdate = encodeTestFullProof(proof);
            }
            return fetch(__classPrivateFieldGet(this, _TestCredential_autotaskWebhook, "f"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    abi: CredentialRegistryABI,
                    functionName: "updateCredential",
                    functionParameters: [__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), credentialUpdate]
                })
            });
        });
    }
    generateRateIssuerProof(identity, rating, comment, semaphoreSnarkArtifacts) {
        return __awaiter(this, void 0, void 0, function* () {
            const credentialsGroup = new Group(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), __classPrivateFieldGet(this, _TestCredential_treeDepth, "f"));
            const credentialsGroupMembers = yield __classPrivateFieldGet(this, _TestCredential_subgraph, "f").getGroupMembers(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"), "credentials");
            credentialsGroup.addMembers(credentialsGroupMembers);
            return generateRateCredentialIssuerProof(identity, credentialsGroup, rating, comment, semaphoreSnarkArtifacts);
        });
    }
    verifyRateIssuerProof(proof) {
        return __awaiter(this, void 0, void 0, function* () {
            return verifyProof(proof.semaphoreFullProof, __classPrivateFieldGet(this, _TestCredential_treeDepth, "f"));
        });
    }
    sendRateIssuerTransaction(proof) {
        return __awaiter(this, void 0, void 0, function* () {
            return fetch(__classPrivateFieldGet(this, _TestCredential_autotaskWebhook, "f"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    abi: CredentialRegistryABI,
                    functionName: "updateCredential",
                    functionParameters: [
                        __classPrivateFieldGet(this, _TestCredential_credentialId, "f"),
                        proof.semaphoreFullProof.merkleTreeRoot,
                        proof.semaphoreFullProof.nullifierHash,
                        proof.semaphoreFullProof.proof,
                        proof.rating,
                        proof.comment
                    ]
                })
            });
        });
    }
    get testCredentialData() {
        return __classPrivateFieldGet(this, _TestCredential_testData, "f");
    }
    get requiredCredential() {
        return __classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredential;
    }
    get requiredCredentialGradeThreshold() {
        return __classPrivateFieldGet(this, _TestCredential_testData, "f").requiredCredentialGradeThreshold;
    }
    get admin() {
        return __classPrivateFieldGet(this, _TestCredential_testData, "f").admin;
    }
    get isValid() {
        return __classPrivateFieldGet(this, _TestCredential_testData, "f").minimumGrade === 255;
    }
    getMerkleTreeRoot(group) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (group) {
                case "grade":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getMerkleTreeRoot(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 1)).toString();
                case "credentials":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getMerkleTreeRoot(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 2)).toString();
                case "no-credentials":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getMerkleTreeRoot(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 3)).toString();
            }
        });
    }
    getNumberOfMerkleTreeLeaves(group) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (group) {
                case "grade":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getNumberOfMerkleTreeLeaves(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 1)).toNumber();
                case "credentials":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getNumberOfMerkleTreeLeaves(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 2)).toNumber();
                case "no-credentials":
                    return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getNumberOfMerkleTreeLeaves(3 * (__classPrivateFieldGet(this, _TestCredential_credentialId, "f") - 1) + 3)).toNumber();
            }
        });
    }
    URI() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getCredentialURI(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"));
        });
    }
    averageRating() {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield __classPrivateFieldGet(this, _TestCredential_credentialsRegistry, "f").getCredentialAverageRating(__classPrivateFieldGet(this, _TestCredential_credentialId, "f"))).toNumber();
        });
    }
}
_TestCredential_poseidon = new WeakMap(), _TestCredential_subgraph = new WeakMap(), _TestCredential_network = new WeakMap(), _TestCredential_credentialId = new WeakMap(), _TestCredential_testData = new WeakMap(), _TestCredential_credentialsRegistry = new WeakMap(), _TestCredential_treeDepth = new WeakMap(), _TestCredential_openAnswersHashes = new WeakMap(), _TestCredential_autotaskWebhook = new WeakMap(), _TestCredential_getOpenAnswersResult = new WeakMap(), _TestCredential_instances = new WeakSet(), _TestCredential_getMultipleChoiceResult = function _TestCredential_getMultipleChoiceResult({ multipleChoiceAnswers }) {
    const answersArray = generateMultipleChoiceAnswers(multipleChoiceAnswers, __classPrivateFieldGet(this, _TestCredential_testData, "f").testHeight);
    // Checking if test is passed and returning the result
    return rootFromLeafArray(__classPrivateFieldGet(this, _TestCredential_poseidon, "f"), answersArray).toString() === __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceRoot ?
        __classPrivateFieldGet(this, _TestCredential_testData, "f").multipleChoiceWeight : 0;
};

const SUPPORTED_TEST_HEIGHTS = [4, 5, 6];
const MAX_GRADE = 100;
const MAX_TREE_DEPTH = 16;
const MAX_COMMENT_LENGTH = 280;
const FIELD_SIZE = "21888242871839275222246405745257275088548364400416034343698204186575808495617";
const DEPLOYED_CONTRACTS = {
    "maticmum": {
        "credentialsRegistryAddress": "0x5A140303E92da80BF96a734fd777957fF02714C4",
        "testCredentialManagerAddress": "0x043c69abf15d154cf0Ffc482f8d63eE7874e1cee",
        "testCredentialType": 0,
        "legacyCredentialManagerAddress": "0x77479918eA3962f8a1EfCc578520582778E272C7",
        "legacyCredentialType": 1,
        "autotaskWebhook": "https://api.defender.openzeppelin.com/autotasks/7142a57f-97fe-4631-9ced-cefa84035b13/runs/webhook/597126c3-7872-41c7-a242-06c3745a6d66/EKQK6DnE6eBYD1KdgS4PKt"
    }
};

export { DEPLOYED_CONTRACTS, FIELD_SIZE, LegacyCredential, MAX_COMMENT_LENGTH, MAX_GRADE, MAX_TREE_DEPTH, SUPPORTED_TEST_HEIGHTS, TestCredential, buildPoseidon, decodeLegacyCredentialData, decodeTestCredentialData, encodeCredentialRestrictedTestFullProof, encodeGradeRestrictedTestFullProof, encodeLegacyCredential, encodeTestCredential, encodeTestFullProof, generateCredentialOwnershipProof, generateCredentialRestrictedTestProof, generateGradeClaimProof, generateGradeRestrictedTestProof, generateMultipleChoiceAnswers, generateOpenAnswers, generateRateCredentialIssuerProof, generateTestProof, getGradeCommitment, hash, packProof, rootFromLeafArray, unpackProof, verifyCredentialOwnershipProof, verifyGradeClaimProof, verifyTestProof };
